import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  CssBaseline, Box, AppBar, Toolbar, Typography, IconButton, Button, Card, CardContent, CardMedia, Grid, Divider, Select, MenuItem, FormControl, InputLabel, Badge, Snackbar, Slide, useMediaQuery, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, TextField, InputAdornment, Container, Link, Paper, Avatar, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/Facebook';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import BlockIcon from '@mui/icons-material/Block';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Drawer from '@mui/material/Drawer';
import { collection, getDocs, query, where, addDoc, doc, serverTimestamp, getDoc, setDoc, increment } from 'firebase/firestore';
import { db } from './firebase';
import AnnouncementBar from './AnnouncementBar';

const LOGO_URL = '/logo-dentifix.png';
const LOGO_LOCAL = '/logo-dentifix.png'; // use this if you add the image to public/

const DEFAULT_SITE_PARAMS = {
  deliveryFee: 9,
  announcementBarEnabled: false,
  announcementMessages: [],
};

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
];

const translations = {
  en: {
    products: 'Products',
    about: 'About',
    contact: 'Contact',
    affordableDentalSolutions: 'Affordable Dental Solutions',
    forEveryone: 'For Everyone',
    heroDesc: 'DentiFix empowers you to take care of your smile without breaking the bank. Get high-quality, self-applied dental products and enjoy a confident, healthy smile at a fraction of the cost.',
    shopNow: 'Shop Now',
    affordable: 'Affordable',
    quality: 'Quality',
    accessible: 'Accessible',
    whyChoose: 'Why Choose DentiFix?',
    whyChooseDesc: 'We believe everyone deserves access to affordable, effective dental care.',
    affordablePricing: 'Affordable Pricing',
    affordablePricingDesc: 'Get the dental care you need for a fraction of the price, with high-quality, self-applied dental products.',
    qualityGuarantee: 'Quality Guarantee',
    qualityGuaranteeDesc: 'All products are safe, easy to use, and designed for self-application. No dental visit required.',
    communityFocus: 'Community Focus',
    communityFocusDesc: 'We empower our community with accessible dental solutions and clear instructions for confident, at-home care.',
    cartSummary: 'Cart Summary',
    subtotal: 'Subtotal:',
    deliveryFee: 'Delivery Fee:',
    freeDelivery: 'Free',
    total: 'Total',
    checkout: 'Checkout',
    haveQuestions: 'Have questions? We\'re here to help you find the right dental solutions',
    callUs: 'Call Us',
    email: 'Email',
    visit: 'Visit',
    supportPhone: '(555) 123-CARE',
    supportHours: 'Mon-Fri 9AM-6PM',
    supportEmail: 'hello@dentifix.com',
    support24: '24/7 Support',
    supportAddress: '123 Dental Street',
    supportLocation: 'Downtown Health Center',
    developedBy: 'Developed by',
    orderInfo: 'Order Information',
    fullName: 'Full Name',
    phoneNumber: 'Phone Number',
    address: 'Address',
    city: 'Location/City',
    totalAmount: 'Total Amount:',
    back: 'Back',
    placeOrder: 'Place Order',
    orderPlaced: 'Order placed! Thank you.',
    fillAllFields: 'Please fill in all required fields.',
    loadingProducts: 'Loading products...',
    emptyCart: 'Your cart is empty.',
    findUs: 'Find Us',
    outOfStock: 'Out of Stock',
    itemUnavailable: 'This item is currently unavailable',
    inStock: 'in stock',
    addToCart: 'Add to Cart',
    promo: 'PROMO',
    off: 'OFF',
    item: 'item',
    items: 'items',
    addedItem: 'Added 1',
    removedItem: 'Removed 1',
    footerDescription: 'DentiFix makes dental care accessible and affordable for everyone. Get high-quality, self-applied dental products for a healthy smile at a fraction of the cost.',
    orderFailed: 'Failed to place order',
    lang: 'Lang',
    toggleTheme: 'Toggle Theme',
  },
  fr: {
    products: 'Produits',
    about: 'À propos',
    contact: 'Contact',
    affordableDentalSolutions: 'Solutions dentaires abordables',
    forEveryone: 'Pour tous',
    heroDesc: 'DentiFix vous permet de prendre soin de votre sourire sans vous ruiner. Obtenez des produits dentaires de haute qualité, à appliquer vous-même, et profitez d\'un sourire sain et confiant à moindre coût.',
    shopNow: 'Acheter',
    affordable: 'Abordable',
    quality: 'Qualité',
    accessible: 'Accessible',
    whyChoose: 'Pourquoi choisir DentiFix ?',
    whyChooseDesc: 'Nous pensons que tout le monde mérite un accès à des soins dentaires abordables et efficaces.',
    affordablePricing: 'Prix abordables',
    affordablePricingDesc: 'Obtenez les soins dentaires dont vous avez besoin à moindre coût, avec des produits dentaires de haute qualité à appliquer vous-même.',
    qualityGuarantee: 'Garantie qualité',
    qualityGuaranteeDesc: 'Tous les produits sont sûrs, faciles à utiliser et conçus pour l\'auto-application. Pas besoin de visite chez le dentiste.',
    communityFocus: 'Communauté',
    communityFocusDesc: 'Nous aidons notre communauté avec des solutions dentaires accessibles et des instructions claires pour des soins à domicile en toute confiance.',
    cartSummary: 'Résumé du panier',
    subtotal: 'Sous-total :',
    deliveryFee: 'Frais de livraison :',
    freeDelivery: 'Gratuit',
    total: 'Total',
    checkout: 'Commander',
    haveQuestions: 'Des questions ? Nous sommes là pour vous aider à trouver les bonnes solutions dentaires',
    callUs: 'Appelez-nous',
    email: 'E-mail',
    visit: 'Visitez',
    supportPhone: '(555) 123-CARE',
    supportHours: 'Lun-Ven 9h-18h',
    supportEmail: 'hello@dentifix.com',
    support24: 'Support 24/7',
    supportAddress: '123 Rue Dentaire',
    supportLocation: 'Centre de santé',
    developedBy: 'Développé par',
    orderInfo: 'Informations de commande',
    fullName: 'Nom complet',
    phoneNumber: 'Numéro de téléphone',
    address: 'Adresse',
    city: 'Ville/Localité',
    totalAmount: 'Montant total :',
    back: 'Retour',
    placeOrder: 'Passer la commande',
    orderPlaced: 'Commande passée ! Merci.',
    fillAllFields: 'Veuillez remplir tous les champs requis.',
    loadingProducts: 'Chargement des produits...',
    emptyCart: 'Votre panier est vide.',
    findUs: 'Retrouvez-nous',
    outOfStock: 'Rupture de stock',
    itemUnavailable: 'Cet article n\'est actuellement pas disponible',
    inStock: 'en stock',
    addToCart: 'Ajouter au panier',
    promo: 'PROMO',
    off: 'RÉDUCTION',
    item: 'article',
    items: 'articles',
    addedItem: 'Ajouté 1',
    removedItem: 'Retiré 1',
    footerDescription: 'DentiFix rend les soins dentaires accessibles et abordables pour tous. Obtenez des produits dentaires de haute qualité, à appliquer vous-même, pour un sourire sain à moindre coût.',
    orderFailed: 'Échec de la commande',
    lang: 'Lang',
    toggleTheme: 'Changer le thème',
  },
  ar: {
    products: 'المنتجات',
    about: 'حول',
    contact: 'اتصل',
    affordableDentalSolutions: 'حلول طب الأسنان بأسعار معقولة',
    forEveryone: 'للجميع',
    heroDesc: 'تمكنك DentiFix من العناية بابتسامتك دون إنفاق الكثير. احصل على منتجات طب أسنان عالية الجودة وسهلة الاستخدام وابتسم بثقة وصحة بتكلفة منخفضة.',
    shopNow: 'تسوق الآن',
    affordable: 'سعر مناسب',
    quality: 'جودة',
    accessible: 'متاح',
    whyChoose: 'لماذا تختار DentiFix؟',
    whyChooseDesc: 'نؤمن أن للجميع الحق في رعاية أسنان فعالة وبأسعار معقولة.',
    affordablePricing: 'أسعار مناسبة',
    affordablePricingDesc: 'احصل على رعاية الأسنان التي تحتاجها بسعر منخفض مع منتجات عالية الجودة وسهلة الاستخدام.',
    qualityGuarantee: 'ضمان الجودة',
    qualityGuaranteeDesc: 'جميع المنتجات آمنة وسهلة الاستخدام ومصممة للاستخدام الذاتي. لا حاجة لزيارة طبيب الأسنان.',
    communityFocus: 'مجتمعنا',
    communityFocusDesc: 'نساعد مجتمعنا بحلول أسنان متاحة وتعليمات واضحة للعناية المنزلية بثقة.',
    cartSummary: 'ملخص السلة',
    subtotal: 'المجموع الفرعي:',
    deliveryFee: 'رسوم التوصيل:',
    freeDelivery: 'مجاني',
    total: 'الإجمالي',
    checkout: 'إتمام الشراء',
    haveQuestions: 'هل لديك أسئلة؟ نحن هنا لمساعدتك في إيجاد الحلول المناسبة',
    callUs: 'اتصل بنا',
    email: 'البريد الإلكتروني',
    visit: 'زيارة',
    supportPhone: '(555) 123-CARE',
    supportHours: 'الإثنين-الجمعة 9ص-6م',
    supportEmail: 'hello@dentifix.com',
    support24: 'دعم 24/7',
    supportAddress: '123 شارع الأسنان',
    supportLocation: 'مركز الصحة',
    developedBy: 'تم التطوير بواسطة',
    orderInfo: 'معلومات الطلب',
    fullName: 'الاسم الكامل',
    phoneNumber: 'رقم الهاتف',
    address: 'العنوان',
    city: 'المدينة/الموقع',
    totalAmount: 'المبلغ الإجمالي:',
    back: 'رجوع',
    placeOrder: 'إرسال الطلب',
    orderPlaced: 'تم إرسال الطلب! شكراً لك.',
    fillAllFields: 'يرجى ملء جميع الحقول المطلوبة.',
    loadingProducts: 'جاري تحميل المنتجات...',
    emptyCart: 'سلتك فارغة.',
    findUs: 'تابعنا',
    outOfStock: 'نفد المخزون',
    itemUnavailable: 'هذا المنتج غير متوفر حالياً',
    inStock: 'متوفر',
    addToCart: 'أضف إلى السلة',
    promo: 'عرض',
    off: 'خصم',
    item: 'منتج',
    items: 'منتجات',
    addedItem: 'تمت إضافة 1',
    removedItem: 'تمت إزالة 1',
    footerDescription: 'تجعل DentiFix رعاية الأسنان في متناول الجميع وبأسعار معقولة. احصل على منتجات طب أسنان عالية الجودة وسهلة الاستخدام للحصول على ابتسامة صحية بتكلفة منخفضة.',
    orderFailed: 'فشل إرسال الطلب',
    lang: 'اللغة',
    toggleTheme: 'تبديل المظهر',
  },
};

const ProductCard = React.memo(function ProductCard({ product, qty, onQtyChange, disabledAdd, disabledRemove, onAddToCart, translations, lang }) {
  const isOutOfStock = product.amount === 0;
  const isInPromo = product.isInPromo || false;
  
  return (
    <Card
      sx={{
        maxWidth: 400,
        width: '100%',
        mx: 'auto',
        mb: 4,
        borderRadius: 4,
        boxShadow: isInPromo ? 12 : 6,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s, transform 0.2s',
        position: 'relative',
        border: isInPromo ? '3px solid' : 'none',
        borderColor: isInPromo ? 'warning.main' : 'transparent',
        opacity: isOutOfStock ? 0.6 : 1,
        '&:hover': { 
          boxShadow: isInPromo ? 16 : 12, 
          transform: isOutOfStock ? 'none' : 'translateY(-2px) scale(1.01)' 
        },
      }}
    >
      {/* Discount Badge */}
      {isInPromo && (
        <Chip
          icon={<LocalOfferIcon />}
          label={translations[lang].promo}
          color="warning"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            fontWeight: 800,
            fontSize: '0.9rem',
            height: 32,
            boxShadow: 4,
            animation: 'pulse 2s infinite',
            gap: 0.5,
            '& .MuiChip-icon': {
              marginRight: lang === 'ar' ? 0 : '4px',
              marginLeft: lang === 'ar' ? '4px' : 0,
            },
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
            },
          }}
        />
      )}
      
      {/* Out of Stock Overlay */}
      {isOutOfStock && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            borderRadius: 4,
          }}
        >
          <BlockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={800} color="error.main" sx={{ mb: 1 }}>
            {translations[lang].outOfStock}
          </Typography>
          <Typography variant="body1" color="rgba(255, 255, 255, 0.9)">
            {translations[lang].itemUnavailable}
          </Typography>
        </Box>
      )}
      
      <Box sx={{ position: 'relative' }}>
      <CardMedia
        component="img"
        image={product.img}
        alt={product.name}
        loading="lazy"
        sx={{
          width: '100%',
          height: 180,
          objectFit: 'contain',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          background: 'transparent',
          display: 'block',
            filter: isOutOfStock ? 'grayscale(100%)' : 'none',
        }}
      />
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{product.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.95rem', sm: '1rem' } }}>{product.desc}</Typography>
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {product.features && product.features.map((feature, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#1e88e5' }} fontSize="small" />
              <Typography variant="body2" color="text.secondary">{feature}</Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: isInPromo ? 'error.main' : 'warning.main', fontSize: { xs: '1.2rem', sm: '1.5rem' }, direction: 'ltr', display: 'inline-block' }}>
              {product.price} DT
            </Typography>
          {product.oldPrice && (
              <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through', fontWeight: 500, fontSize: { xs: '1rem', sm: '1.1rem' }, direction: 'ltr', display: 'inline-block' }}>
                {product.oldPrice} DT
            </Typography>
          )}
            {isInPromo && product.oldPrice && (
              <Chip 
                label={`${Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)}% ${translations[lang].off}`}
                color="error"
                size="small"
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.75rem',
                  padding: lang === 'ar' ? '0 4px 0 8px' : '0 8px 0 4px',
                }}
              />
            )}
          </Box>
          <Typography 
            variant="body2" 
            color={isOutOfStock ? 'error.main' : 'text.secondary'}
            sx={{ 
              ml: 'auto',
              fontWeight: isOutOfStock ? 700 : 400,
            }}
          >
            {isOutOfStock ? translations[lang].outOfStock : `${product.amount} ${translations[lang].inStock}`}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <IconButton onClick={() => onQtyChange(product.id, -1)} color="primary" size="large" disabled={disabledRemove || isOutOfStock} sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper', minWidth: 36, minHeight: 36 }}>
            <RemoveIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{qty}</Typography>
          <IconButton onClick={() => onQtyChange(product.id, 1)} color="primary" size="large" disabled={disabledAdd || isOutOfStock} sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper', minWidth: 36, minHeight: 36 }}>
            <AddIcon />
          </IconButton>
          <Button
            variant="contained"
            color={isInPromo ? "error" : "warning"}
            size="large"
            sx={{ 
              flex: 1, 
              ml: 2, 
              borderRadius: 2, 
              fontWeight: 700, 
              fontSize: { xs: '1rem', sm: '1.1rem' }, 
              boxShadow: isInPromo ? 4 : 2, 
              py: 1, 
              color: '#fff', 
              minHeight: 44,
              position: 'relative',
              overflow: 'hidden',
              '&:disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              }
            }}
            disabled={disabledAdd || isOutOfStock}
            onClick={() => { onQtyChange(product.id, 1); onAddToCart(); }}
          >
            {isOutOfStock ? translations[lang].outOfStock : translations[lang].addToCart}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
});

export default function DentiFixPage() {
  // Initialize language from localStorage or default to Arabic
  const [lang, setLang] = useState(() => {
    const savedLang = localStorage.getItem('dentifix-lang');
    return savedLang || 'ar'; // Default to Arabic
  });
  
  const [cart, setCart] = useState({});
  const [darkMode, setDarkMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [orderInfo, setOrderInfo] = useState({ name: '', phone: '', address: '', city: '' });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartExpanded, setCartExpanded] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [siteParams, setSiteParams] = useState(DEFAULT_SITE_PARAMS);

  // Persist language changes to localStorage
  const handleLangChange = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('dentifix-lang', newLang);
  }, []);

  // Update document direction based on language (RTL for Arabic, LTR for others)
  useEffect(() => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#1e88e5' },
      secondary: { main: '#fff200' },
      warning: { main: '#ffb300' },
      background: { default: darkMode ? '#181A20' : '#fffbe7' },
    },
    typography: {
      fontFamily: 'Quicksand, Nunito, Inter, Arial, sans-serif',
      fontWeightBold: 700,
      fontWeightMedium: 600,
      fontWeightRegular: 500,
    },
    shape: {
      borderRadius: 12,
    },
  }), [darkMode]);

  const handleQty = useCallback((id, delta) => {
    setCart(prev => {
      const qty = (prev[id] || 0) + delta;
      if (qty < 0) return prev;
      const product = products.find(p => p.id === id);
      if (qty > product.amount) return prev;
      return { ...prev, [id]: qty };
    });
  }, [products, lang]);

  const basePrice = useMemo(() => products.reduce((sum, p) => sum + (cart[p.id] || 0) * p.price, 0), [cart, products]);
  const deliveryFee = siteParams.deliveryFee ?? DEFAULT_SITE_PARAMS.deliveryFee;
  const appliedDeliveryFee = basePrice > 0 ? deliveryFee : 0;
  const total = basePrice > 0 ? basePrice + appliedDeliveryFee : 0;
  const totalItems = useMemo(() => Object.values(cart).reduce((a, b) => a + b, 0), [cart]);
  const cartItems = useMemo(() => products.filter(p => cart[p.id]), [cart]);

  const formatDeliveryFee = () => {
    if (basePrice <= 0) return '0.00 DT';
    if (appliedDeliveryFee === 0) return translations[lang].freeDelivery;
    return `${appliedDeliveryFee.toFixed(2)} DT`;
  };

  const handleOrderInput = (e) => {
    setOrderInfo({ ...orderInfo, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async () => {
    if (placingOrder) return;
    if (!orderInfo.name || !orderInfo.phone || !orderInfo.address || !orderInfo.city) {
      setSnackbar({ open: true, message: translations[lang].fillAllFields });
      return;
    }
    setPlacingOrder(true);
    try {
      const itemsRefs = Object.entries(cart)
        .filter(([id, qty]) => qty > 0)
        .map(([id]) => doc(db, 'inStore', id));
      await addDoc(collection(db, 'orders'), {
        username: orderInfo.name,
        number: orderInfo.phone,
        DeliveryAdress: orderInfo.address + '، ' + orderInfo.city,
        subtotal: basePrice,
        deliveryFee: appliedDeliveryFee,
        total: total,
        items: itemsRefs,
        status: 0,
        Note: '',
        brand: 'dentifix',
        Timestamp: serverTimestamp(),
      });
      setOrderFormOpen(false);
      setSnackbar({ open: true, message: translations[lang].orderPlaced });
      setCart({});
      setOrderInfo({ name: '', phone: '', address: '', city: '' });
    } catch (err) {
      setSnackbar({ open: true, message: `${translations[lang].orderFailed}: ${err.message}` });
    } finally {
      setPlacingOrder(false);
    }
  };

  // Track unique visitor - runs on every page load/mount regardless of URL hash
  useEffect(() => {
    let isMounted = true;
    
    async function trackVisitor() {
      try {
        // Generate or retrieve visitor ID
        let visitorId = localStorage.getItem('dentifix-visitor-id');
        if (!visitorId) {
          // Generate unique ID
          visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('dentifix-visitor-id', visitorId);
        }

        // Get today's date
        const today = new Date().toISOString().split('T')[0];
        const lastVisitDate = localStorage.getItem('dentifix-last-visit');
        
        // Track if this is a new visit today (not just a page refresh/navigation)
        // We check both the date and a small delay to prevent multiple rapid fires
        const lastVisitTimestamp = localStorage.getItem('dentifix-last-visit-timestamp');
        const now = Date.now();
        const isNewVisit = lastVisitDate !== today || 
                          !lastVisitTimestamp || 
                          (now - parseInt(lastVisitTimestamp)) > 60000; // 1 minute minimum between visits

        if (isNewVisit && isMounted) {
          // Update last visit tracking
          localStorage.setItem('dentifix-last-visit', today);
          localStorage.setItem('dentifix-last-visit-timestamp', now.toString());

          // Check if this visitor already visited today BEFORE adding new visit
          const statsDocRef = doc(db, 'stats', today);
          const statsDoc = await getDoc(statsDocRef);
          const existingVisits = await getDocs(
            query(collection(db, 'visits'), where('date', '==', today), where('visitorId', '==', visitorId))
          );
          const isNewUniqueVisitor = existingVisits.size === 0;

          // Track visit in Firestore
          const visitDoc = {
            visitorId: visitorId,
            timestamp: serverTimestamp(),
            date: today,
            brand: 'dentifix',
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            url: window.location.href,
            path: window.location.pathname + window.location.hash,
          };

          await addDoc(collection(db, 'visits'), visitDoc);

          // Update daily stats
          if (statsDoc.exists()) {
            if (isNewUniqueVisitor) {
              // New unique visitor for today
              await setDoc(statsDocRef, {
                date: today,
                uniqueVisitors: increment(1),
                totalVisits: increment(1),
              }, { merge: true });
            } else {
              // Returning visitor, just increment total visits
              await setDoc(statsDocRef, {
                totalVisits: increment(1),
              }, { merge: true });
    }
          } else {
            // First visit of the day
            await setDoc(statsDocRef, {
              date: today,
              uniqueVisitors: 1,
              totalVisits: 1,
            });
          }
        }
      } catch (err) {
        console.error('Error tracking visitor:', err);
        // Fail silently to not disrupt user experience
      }
    }
    
    // Small delay to ensure page is fully loaded and auth is ready
    const timeoutId = setTimeout(() => {
      trackVisitor();
    }, 100);
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []); // Empty deps means it runs once on mount, regardless of URL changes

  useEffect(() => {
    let isMounted = true;

    async function fetchSiteParams() {
      try {
        let snapshot = await getDoc(doc(db, 'params', 'dentifix'));
        if (!snapshot.exists()) {
          snapshot = await getDoc(doc(db, 'params', 'site'));
        }
        if (!isMounted) return;
        if (snapshot.exists()) {
          const data = snapshot.data();
          setSiteParams({
            deliveryFee: typeof data.deliveryFee === 'number' ? data.deliveryFee : DEFAULT_SITE_PARAMS.deliveryFee,
            announcementBarEnabled: Boolean(data.announcementBarEnabled),
            announcementMessages: Array.isArray(data.announcementMessages)
              ? data.announcementMessages
              : [],
          });
        }
      } catch (err) {
        console.error('Failed to fetch site params:', err);
      }
    }

    fetchSiteParams();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    async function fetchProducts() {
      setLoading(true);
      setFetchError(null);
      try {
        const q = query(collection(db, 'inStore'), where('isAvailalble', '==', true));
        const querySnapshot = await getDocs(q);
        
        if (!isMounted) return; // Don't update state if component unmounted
        
        const docs = querySnapshot.docs
          .map(doc => {
          const d = doc.data();
          if (d.brand && d.brand !== 'dentifix') return null;
          return {
            id: doc.id,
            name: d.ItemName,
            price: d.isInPromo ? d.PromoPrice : d.ItemPrice,
            oldPrice: d.isInPromo ? d.ItemPrice : undefined,
            amount: d.ItemQuantity,
            img: d.ImageLink,
            desc: d.ItemDescription,
            features: [],
            isInPromo: d.isInPromo || false,
            badge: d.isInPromo ? 'Promo' : '',
            listorder: d.listorder ?? 9999,
            createdAt: doc.id,
          };
        }).filter(Boolean);
        // Sort by listorder (ascending), then by newest first (document ID contains timestamp)
        docs.sort((a, b) => {
          if (a.listorder !== b.listorder) {
            return (a.listorder ?? 9999) - (b.listorder ?? 9999);
          }
          // If same listorder, sort by newest first (larger ID = newer)
          return b.createdAt.localeCompare(a.createdAt);
        });
        setProducts(docs);
      } catch (err) {
        if (!isMounted) return;
        setFetchError('Failed to fetch products from Firestore: ' + err.message);
        setProducts([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    fetchProducts();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Generate structured data for SEO
  const structuredData = useMemo(() => {
    const siteUrl = 'https://dentifix.com';
    const logoUrl = `${siteUrl}/logo-dentifix.png`;
    
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'DentiFix',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: logoUrl,
            width: 512,
            height: 512
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: translations[lang].supportPhone,
            contactType: 'Customer Service',
            email: translations[lang].supportEmail,
            availableLanguage: ['en', 'fr', 'ar']
          },
          sameAs: [
            // Add your social media URLs here when available
            // 'https://www.facebook.com/dentifix',
            // 'https://www.instagram.com/dentifix',
          ]
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'DentiFix',
          description: translations[lang].heroDesc,
          publisher: {
            '@id': `${siteUrl}/#organization`
          },
          inLanguage: ['en', 'fr', 'ar'],
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${siteUrl}/?search={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          }
        },
        {
          '@type': ['Store', 'Organization'],
          '@id': `${siteUrl}/#store`,
          name: 'DentiFix',
          image: logoUrl,
          address: {
            '@type': 'PostalAddress',
            streetAddress: translations[lang].supportAddress,
            addressLocality: translations[lang].supportLocation
          },
          telephone: translations[lang].supportPhone,
          email: translations[lang].supportEmail,
          priceRange: '$$',
          acceptsReservations: false,
          currenciesAccepted: 'TND',
          paymentAccepted: 'Cash, Credit Card'
        },
        ...products.slice(0, 10).map(product => ({
          '@type': 'Product',
          '@id': `${siteUrl}/#product-${product.id}`,
          name: product.name,
          description: product.desc || `${product.name} - ${translations[lang].affordableDentalSolutions}`,
          image: product.img || logoUrl,
          brand: {
            '@type': 'Brand',
            name: 'DentiFix'
          },
          offers: {
            '@type': 'Offer',
            url: `${siteUrl}/#products`,
            priceCurrency: 'TND',
            price: product.price,
            availability: product.amount > 0 
              ? 'https://schema.org/InStock' 
              : 'https://schema.org/OutOfStock',
            itemCondition: 'https://schema.org/NewCondition',
            priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.5',
            reviewCount: '100'
          }
        }))
      ]
    };
  }, [products, lang]);

  const currentTitle = useMemo(() => {
    const baseTitle = 'DentiFix - Affordable Dental Solutions For Everyone';
    const langTitles = {
      en: baseTitle,
      fr: 'DentiFix - Solutions Dentaires Abordables Pour Tous',
      ar: 'DentiFix - حلول الأسنان بأسعار معقولة للجميع'
    };
    return langTitles[lang] || baseTitle;
  }, [lang]);

  const currentDescription = useMemo(() => {
    return translations[lang].heroDesc || translations.en.heroDesc;
  }, [lang]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', position: 'relative' }}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{currentTitle}</title>
        <meta name="description" content={currentDescription} />
        <meta name="keywords" content="dental products, affordable dental care, self-applied dental products, dental solutions, oral care, dental hygiene, teeth whitening, dental health, affordable dentistry, at-home dental care, DentiFix" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={currentTitle} />
        <meta property="og:description" content={currentDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dentifix.com/" />
        <meta property="og:image" content="https://dentifix.com/logo-dentifix.png" />
        <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang === 'fr' ? 'fr_FR' : 'ar_AR'} />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:locale:alternate" content="fr_FR" />
        <meta property="og:locale:alternate" content="ar_AR" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentTitle} />
        <meta name="twitter:description" content={currentDescription} />
        <meta name="twitter:image" content="https://dentifix.com/logo-dentifix.png" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://dentifix.com/" />
        
        {/* Alternate Language Links */}
        <link rel="alternate" hreflang="en" href="https://dentifix.com/?lang=en" />
        <link rel="alternate" hreflang="fr" href="https://dentifix.com/?lang=fr" />
        <link rel="alternate" hreflang="ar" href="https://dentifix.com/?lang=ar" />
        <link rel="alternate" hreflang="x-default" href="https://dentifix.com/" />
        
        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <CssBaseline />
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
        {siteParams.announcementBarEnabled && (
          <AnnouncementBar
            messages={siteParams.announcementMessages}
            darkMode={darkMode}
          />
        )}
      {/* Top Navigation Bar */}
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 72 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img src={LOGO_URL} alt="DentiFix Logo" style={{ width: 48, height: 48, marginRight: 12 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: 'warning.main', letterSpacing: 1 }}>DentiFix</Typography>
          </Box>
          {/* Desktop Nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            <Link href="#products" underline="none" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem', '&:hover': { color: 'primary.main' } }}>{translations[lang].products}</Link>
            <Link href="#about" underline="none" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem', '&:hover': { color: 'primary.main' } }}>{translations[lang].about}</Link>
            <FormControl size="small" variant="outlined">
              <InputLabel id="lang-label">{translations[lang].lang}</InputLabel>
              <Select labelId="lang-label" value={lang} onChange={e => handleLangChange(e.target.value)} label={translations[lang].lang}>
                {LANGUAGES.map(l => (
                  <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={() => setDarkMode(m => !m)} color="primary">
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <Badge badgeContent={totalItems} color="warning" showZero>
              <IconButton onClick={() => { document.getElementById('products').scrollIntoView({ behavior: 'smooth' }); }}>
                <ShoppingCartIcon sx={{ color: '#1e88e5' }} fontSize="large" />
              </IconButton>
            </Badge>
          </Box>
          {/* Mobile Nav: Hamburger */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setDrawerOpen(true)} color="primary">
              <MenuIcon fontSize="large" />
            </IconButton>
            <Badge badgeContent={totalItems} color="warning" showZero>
              <IconButton onClick={() => { document.getElementById('products').scrollIntoView({ behavior: 'smooth' }); }}>
                <ShoppingCartIcon sx={{ color: '#1e88e5' }} fontSize="large" />
              </IconButton>
            </Badge>
          </Box>
        </Toolbar>
      </AppBar>
      </Box>
      {/* Drawer for mobile nav */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Link href="#products" underline="none" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem' }} onClick={() => setDrawerOpen(false)}>{translations[lang].products}</Link>
            <Link href="#about" underline="none" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem' }} onClick={() => setDrawerOpen(false)}>{translations[lang].about}</Link>
            <FormControl size="small" variant="outlined" fullWidth sx={{ mt: 2 }}>
              <InputLabel id="lang-label-mobile">{translations[lang].lang}</InputLabel>
              <Select labelId="lang-label-mobile" value={lang} onChange={e => handleLangChange(e.target.value)} label={translations[lang].lang}>
                {LANGUAGES.map(l => (
                  <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              onClick={() => setDarkMode(m => !m)} 
              color="primary" 
              variant="outlined" 
              startIcon={darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              sx={{
                '& .MuiButton-startIcon': {
                  marginRight: lang === 'ar' ? 0 : '8px',
                  marginLeft: lang === 'ar' ? '8px' : 0,
                },
              }}
            >
              {translations[lang].toggleTheme}
            </Button>
          </Stack>
        </Box>
      </Drawer>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'background.default', pt: 6, pb: 0, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Button 
            startIcon={<AttachMoneyIcon />} 
            variant="outlined" 
            color="primary" 
            sx={{ 
              mb: 1, 
              borderRadius: 3, 
              fontWeight: 700, 
              px: 3, 
              py: 1.5,
              fontSize: { xs: '1.1rem', sm: '1.3rem' },
              '& .MuiButton-startIcon': {
                marginRight: lang === 'ar' ? 0 : '8px',
                marginLeft: lang === 'ar' ? '8px' : 0,
              },
            }}
          >
            {translations[lang].affordableDentalSolutions}
          </Button>
        </Container>
      </Box>
      {/* Product Cards Section */}
      <Box id="products" sx={{ bgcolor: 'background.default', pt: 2, pb: 6 }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Typography variant="h6">{translations[lang].loadingProducts}</Typography>
            </Box>
          ) : fetchError ? (
            <Box sx={{ color: 'error.main', textAlign: 'center', mb: 2 }}>
              <Typography variant="body1">{fetchError}</Typography>
            </Box>
          ) : (
            <Grid container spacing={4} justifyContent="center">
              {products.map(product => (
                <Grid item xs={12} sm={8} md={4} key={product.id} display="flex" justifyContent="center">
                  <ProductCard
                    product={product}
                    qty={cart[product.id] || 0}
                    onQtyChange={handleQty}
                    disabledAdd={(cart[product.id] || 0) >= product.amount}
                    disabledRemove={(cart[product.id] || 0) === 0}
                    onAddToCart={() => {}}
                    translations={translations}
                    lang={lang}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
      {/* Why Choose Section */}
      <Box id="about" sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 2 }}>{translations[lang].whyChoose}</Typography>
          <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 6 }}>
            {translations[lang].whyChooseDesc}
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56, mb: 2 }}><AttachMoneyIcon fontSize="large" /></Avatar>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{translations[lang].affordablePricing}</Typography>
                <Typography color="text.secondary">{translations[lang].affordablePricingDesc}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mb: 2 }}><VerifiedUserIcon fontSize="large" /></Avatar>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{translations[lang].qualityGuarantee}</Typography>
                <Typography color="text.secondary">{translations[lang].qualityGuaranteeDesc}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56, mb: 2 }}><GroupsIcon fontSize="large" /></Avatar>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{translations[lang].communityFocus}</Typography>
                <Typography color="text.secondary">{translations[lang].communityFocusDesc}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* Find Us Section */}
      <Box id="find-us" sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4 }}>
            {translations[lang].findUs}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<FacebookIcon />}
              href="https://www.facebook.com/profile.php?id=61562676658659"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#1877f2',
                color: '#fff',
                '&:hover': { bgcolor: '#166fe5' },
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                boxShadow: 4,
                minWidth: 180,
                gap: 1,
                '& .MuiButton-startIcon': {
                  marginRight: lang === 'ar' ? 0 : '8px',
                  marginLeft: lang === 'ar' ? '8px' : 0,
                },
              }}
            >
              Facebook
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<MusicNoteIcon />}
              href="https://www.tiktok.com/@dentifix"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#000',
                color: '#fff',
                '&:hover': { bgcolor: '#333' },
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                boxShadow: 4,
                minWidth: 180,
                gap: 1,
                '& .MuiButton-startIcon': {
                  marginRight: lang === 'ar' ? 0 : '8px',
                  marginLeft: lang === 'ar' ? '8px' : 0,
                },
              }}
            >
              TikTok
            </Button>
            </Box>
        </Container>
      </Box>
      {/* Floating Cart Summary */}
      {cartItems.length > 0 && (
        <>
          {/* Collapsed bar - always visible */}
          {!cartExpanded && (
            <Box
              onClick={() => setCartExpanded(true)}
              sx={{
          position: 'fixed',
          right: { xs: 'auto', sm: 32 },
                left: { xs: '50%', sm: 'auto' },
          bottom: { xs: 0, sm: 32 },
                transform: { xs: 'translateX(-50%)', sm: 'none' },
          zIndex: 1300,
          width: { xs: 320, sm: 320 },
          maxWidth: { xs: 'calc(100vw - 16px)', sm: 320 },
                bgcolor: 'background.paper',
                borderTop: '3px solid',
                borderColor: 'warning.main',
                color: 'text.primary',
                p: 1.5,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                cursor: 'pointer',
                boxShadow: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: { xs: 'translateX(-50%) translateY(-2px)', sm: 'translateY(-2px)' },
                  boxShadow: 12,
                  borderColor: 'warning.dark',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ShoppingCartIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {translations[lang].cartSummary}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalItems} {totalItems === 1 ? translations[lang].item : translations[lang].items} • <span style={{ direction: 'ltr', display: 'inline-block' }}>{total.toFixed(2)} DT</span>
                  </Typography>
                </Box>
              </Box>
              <ExpandLessIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            </Box>
          )}
          
          {/* Expanded cart */}
          <Box
            sx={{
              position: 'fixed',
              right: { xs: 'auto', sm: 32 },
              left: { xs: '50%', sm: 'auto' },
              bottom: { xs: 0, sm: 32 },
              transform: cartExpanded 
                ? { xs: 'translateX(-50%) translateY(0)', sm: 'translateY(0)' }
                : { xs: 'translateX(-50%) translateY(100%)', sm: 'translateY(100%)' },
              zIndex: 1300,
              width: { xs: 320, sm: 320 },
              maxWidth: { xs: 'calc(100vw - 16px)', sm: 320 },
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: 8,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: { xs: 0, sm: 16 },
          borderBottomRightRadius: { xs: 0, sm: 16 },
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              opacity: cartExpanded ? 1 : 0,
              maxHeight: cartExpanded ? '80vh' : 0,
              pointerEvents: cartExpanded ? 'auto' : 'none',
            }}
          >
            {/* Header with collapse button */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderBottom: '2px solid',
                borderColor: 'divider',
                color: 'text.primary',
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => setCartExpanded(false)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon />
                <Typography variant="subtitle1" fontWeight={700}>
                  {translations[lang].cartSummary}
                </Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={totalItems} color="warning" showZero>
                  <Box sx={{ width: 24, height: 24 }} />
                </Badge>
                <ExpandMoreIcon />
              </Box>
            </Box>
            
            {/* Cart content */}
            <Box sx={{ p: 2, maxHeight: 'calc(80vh - 80px)', overflowY: 'auto' }}>
              <Divider sx={{ mb: 1.5, mt: -0.5 }} />
              
          {/* Cart items description */}
              <Box sx={{ mb: 1.5 }}>
            {cartItems.map(item => (
                  <Typography key={item.id} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {item.name} x{cart[item.id]} (<span style={{ direction: 'ltr', display: 'inline-block' }}>{(item.price * cart[item.id]).toFixed(2)} DT</span>)
              </Typography>
            ))}
          </Box>
              
              <Divider sx={{ my: 1.5 }} />
              
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body1">{translations[lang].subtotal}</Typography>
                <Typography variant="body1" sx={{ direction: 'ltr', display: 'inline-block' }}>{basePrice.toFixed(2)} DT</Typography>
          </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2">{translations[lang].deliveryFee}</Typography>
                <Typography variant="body2" sx={{ direction: 'ltr', display: 'inline-block' }}>{formatDeliveryFee()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>{translations[lang].total}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ direction: 'ltr', display: 'inline-block' }}>{total.toFixed(2)} DT</Typography>
          </Box>
              
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '1.1rem', boxShadow: 2, py: 1.2 }}
            onClick={() => setOrderFormOpen(true)}
          >
            {translations[lang].checkout}
          </Button>
        </Box>
      </Box>
        </>
      )}
      {/* Footer */}
      <Box sx={{ bgcolor: '#232a34', color: '#fff', pt: 6, pb: 3, mt: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img src={LOGO_URL} alt="DentiFix Logo" style={{ width: 40, height: 40, marginRight: 10 }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'warning.main' }}>DentiFix</Typography>
              </Box>
              <Typography color="#b0b8c1">{translations[lang].footerDescription}</Typography>
            </Grid>
            <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Typography variant="subtitle1" color="#b0b8c1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                {translations[lang].developedBy} <span style={{ color: '#fff' }}>Photon369</span>
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* Order Form Modal */}
      <Dialog open={orderFormOpen} onClose={placingOrder ? undefined : () => setOrderFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{translations[lang].orderInfo}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={translations[lang].fullName}
              name="name"
              value={orderInfo.name}
              onChange={handleOrderInput}
              disabled={placingOrder}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="primary" /></InputAdornment> }}
              placeholder={translations[lang].fullName}
              autoComplete="name"
              variant="outlined"
            />
            <TextField
              label={translations[lang].phoneNumber}
              name="phone"
              value={orderInfo.phone}
              onChange={handleOrderInput}
              disabled={placingOrder}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="success" /></InputAdornment> }}
              placeholder={translations[lang].phoneNumber}
              autoComplete="tel"
              variant="outlined"
            />
            <TextField
              label={translations[lang].address}
              name="address"
              value={orderInfo.address}
              onChange={handleOrderInput}
              disabled={placingOrder}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><HomeIcon color="error" /></InputAdornment> }}
              placeholder={translations[lang].address}
              autoComplete="street-address"
              variant="outlined"
            />
            <TextField
              label={translations[lang].city}
              name="city"
              value={orderInfo.city}
              onChange={handleOrderInput}
              disabled={placingOrder}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon color="secondary" /></InputAdornment> }}
              placeholder={translations[lang].city}
              autoComplete="address-level2"
              variant="outlined"
            />
            <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{translations[lang].subtotal}</Typography>
                <Typography variant="body2" sx={{ direction: 'ltr', display: 'inline-block' }}>{basePrice.toFixed(2)} DT</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{translations[lang].deliveryFee}</Typography>
                <Typography variant="body2" sx={{ direction: 'ltr', display: 'inline-block' }}>{formatDeliveryFee()}</Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">{translations[lang].totalAmount}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ direction: 'ltr', display: 'inline-block' }}>{total.toFixed(2)} DT</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button 
            onClick={() => setOrderFormOpen(false)} 
            variant="outlined" 
            color="primary" 
            disabled={placingOrder}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {translations[lang].back}
          </Button>
          <Button 
            onClick={handlePlaceOrder} 
            variant="contained" 
            color="primary" 
            disabled={placingOrder}
            sx={{ fontWeight: 700, borderRadius: 2, minWidth: 120 }}
            startIcon={placingOrder ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {placingOrder ? translations[lang].loadingProducts : translations[lang].placeOrder}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1800}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      />
      </Box>
    </ThemeProvider>
  );
} 